import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { AREA_KEYS, AREAS, PERMISSION_PRESETS, effectivePermissions, presetOf, type Area, type Permissions, type PermissionPreset, type StoredPermissions, type TenantRole } from "@/lib/api";
import { Button, cx } from "./ui";

import { t, msg } from "@/lib/i18n";
export type Choice = "admin" | PermissionPreset | "custom";
const LEVEL_LABEL = { none: "Kein Zugriff", view: "Ansehen", edit: "Bearbeiten" } as const;

export const choiceLabel = (c: string | undefined) =>
  c === "admin" ? "Administrator" : c === "custom" ? "Individuell" : c && c in PERMISSION_PRESETS ? PERMISSION_PRESETS[c as PermissionPreset].label : "Mitarbeiter";

/** Auswahl → Rolle + gespeicherte Rechte */
export function toRolePermissions(choice: Choice, custom: Permissions): { role: TenantRole; permissions: StoredPermissions | null } {
  if (choice === "admin") return { role: "customer_admin", permissions: null };
  const areas = choice === "custom" ? custom : PERMISSION_PRESETS[choice].permissions;
  return { role: "customer_user", permissions: { preset: choice, areas } };
}

/** Rolle/Vorlage wählen, bei „Individuell“ je Bereich festlegen */
export function PermissionsPicker({ choice, onChoice, custom, onCustom, allowAdmin = true, hideAreas = [] }: { choice: Choice; onChoice: (c: Choice) => void; custom: Permissions; onCustom: (p: Permissions) => void; allowAdmin?: boolean; hideAreas?: Area[] }) {
  const options: { key: Choice; label: string; text: string }[] = [
    ...(allowAdmin ? [{ key: "admin" as Choice, label: msg("Administrator"), text: msg("Alles, inkl. Shops, Regeln, Team und Module") }] : []),
    ...(Object.entries(PERMISSION_PRESETS) as [PermissionPreset, (typeof PERMISSION_PRESETS)[PermissionPreset]][]).map(([k, v]) => ({ key: k as Choice, label: v.label, text: v.description })),
    { key: "custom", label: msg("Individuell"), text: msg("Je Bereich selbst festlegen") },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label={t("Rolle")}>
        {options.map((o) => (
          <button key={o.key} type="button" role="radio" aria-checked={choice === o.key} onClick={() => onChoice(o.key)} className={cx("flex items-start gap-2.5 p-2.5 rounded-xl border text-left", choice === o.key ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}>
            <span className={cx("mt-0.5 size-4 rounded-full border-2 grid place-items-center shrink-0", choice === o.key ? "border-ink" : "border-border-strong")}>{choice === o.key && <span className="size-2 rounded-full bg-ink" />}</span>
            <span><span className="block font-medium text-[13px]">{t(o.label)}</span><span className="block text-[11.5px] text-fg-muted leading-snug">{t(o.text)}</span></span>
          </button>
        ))}
      </div>
      {choice !== "admin" && (
        <div className="rounded-xl border border-border overflow-hidden">
          {AREA_KEYS.filter((a) => !hideAreas.includes(a)).map((a) => {
            const current = choice === "custom" ? custom[a] : PERMISSION_PRESETS[choice].permissions[a];
            return (
              <div key={a} className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border last:border-0 text-[13px]">
                <span className="min-w-0 flex-1 basis-40"><span className="font-medium">{t(AREAS[a].label)}</span><span className="block text-[11.5px] text-fg-subtle">{t(AREAS[a].description)}</span></span>
                <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
                  {AREAS[a].levels.map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => { onCustom({ ...(choice === "custom" ? custom : PERMISSION_PRESETS[choice].permissions), [a]: l } as Permissions); onChoice("custom"); }}
                      aria-pressed={current === l}
                      className={cx("h-6 px-2 rounded-md text-[11.5px] font-medium", current === l ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg")}
                    >
                      {LEVEL_LABEL[l]}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Dialog: Rechte einer Person ändern */
export function PermissionsDialog({ member, onClose, onSave }: { member: { email: string; name: string | null; role: TenantRole; permissions?: Permissions; preset?: string }; onClose: () => void; onSave: (v: { role: TenantRole; permissions: StoredPermissions | null }) => Promise<void> }) {
  const start = member.permissions ?? effectivePermissions(member.role);
  const [choice, setChoice] = useState<Choice>(member.role === "customer_admin" ? "admin" : (presetOf(start) as Choice));
  const [custom, setCustom] = useState<Permissions>(start);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await onSave(toRolePermissions(choice, custom));
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(640px,calc(100vw-32px))] max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold">{t("Rechte von")} {member.name ?? member.email}</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1 text-[13px]">{t("Gilt sofort. Was jemand nicht sehen darf, erscheint auch nicht in Navigation, Handlungsbedarf und Benachrichtigungen.")}</Dialog.Description>
          <div className="mt-4"><PermissionsPicker choice={choice} onChoice={setChoice} custom={custom} onCustom={setCustom} /></div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>{t("Abbrechen")}</Button>
            <Button variant="primary" loading={busy} onClick={() => void save()}>{t("Speichern")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const areaLevelSummary = (p: Permissions | undefined) =>
  p ? AREA_KEYS.filter((a: Area) => p[a] !== "none" && a !== "settings").map((a) => `${AREAS[a].label}${p[a] === "view" && AREAS[a].levels.includes("edit" as never) ? " (ansehen)" : ""}`).join(", ") : "";
